// client/src/components/admin/AdminDashboard.jsx - UPDATED WITH FIXED TABLE
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import DoctorDetailsModal from './DoctorDetailsModal';
import adminService from '../../services/adminService';
import '../../styles/components/admin/AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalProfessionals: 0,
    pendingVerifications: 0,
    journalEntries: 0,
  });
  
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Inline styles matching your other admin components
  const styles = {
    tableContainer: {
      marginBottom: '30px',
      overflowX: 'auto'
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: '0 10px'
    },
    tableHead: {
      backgroundColor: 'rgba(167, 215, 197, 0.1)'
    },
    tableHeadCell: {
      padding: '12px 16px',
      textAlign: 'left',
      borderTop: '1px solid #A7D7C5',
      borderBottom: '1px solid #A7D7C5',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      color: '#548170',
      fontWeight: 700
    },
    tableHeadCellFirst: {
      padding: '12px 16px',
      textAlign: 'left',
      borderTop: '1px solid #A7D7C5',
      borderBottom: '1px solid #A7D7C5',
      borderLeft: '1px solid #A7D7C5',
      borderTopLeftRadius: '12px',
      borderBottomLeftRadius: '12px',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      color: '#548170',
      fontWeight: 700
    },
    tableHeadCellLast: {
      padding: '12px 16px',
      textAlign: 'left',
      borderTop: '1px solid #A7D7C5',
      borderBottom: '1px solid #A7D7C5',
      borderRight: '1px solid #A7D7C5',
      borderTopRightRadius: '12px',
      borderBottomRightRadius: '12px',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      color: '#548170',
      fontWeight: 700
    },
    tableRow: {
      backgroundColor: '#ffffff'
    },
    tableRowAlt: {
      backgroundColor: 'rgba(167, 215, 197, 0.1)'
    },
    tableCell: {
      padding: '12px 16px',
      borderTop: '1px solid #A7D7C5',
      borderBottom: '1px solid #A7D7C5',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      verticalAlign: 'middle'
    },
    tableCellFirst: {
      padding: '12px 16px',
      borderTop: '1px solid #A7D7C5',
      borderBottom: '1px solid #A7D7C5',
      borderLeft: '1px solid #A7D7C5',
      borderTopLeftRadius: '12px',
      borderBottomLeftRadius: '12px',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      verticalAlign: 'middle'
    },
    tableCellLast: {
      padding: '12px 16px',
      borderTop: '1px solid #A7D7C5',
      borderBottom: '1px solid #A7D7C5',
      borderRight: '1px solid #A7D7C5',
      borderTopRightRadius: '12px',
      borderBottomRightRadius: '12px',
      fontSize: '15px',
      fontFamily: 'IBM Plex Mono, monospace',
      verticalAlign: 'middle'
    },
    actionButton: {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      border: '1px solid #A7D7C5',
      backgroundColor: 'rgba(167, 215, 197, 0.1)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px'
    },
    tenantInfo: {
      fontSize: '12px',
      color: '#666',
      marginTop: '4px'
    },
    emptyState: {
      backgroundColor: '#fff',
      border: '1px solid #A7D7C5',
      borderRadius: '12px',
      padding: '40px',
      textAlign: 'center',
      marginBottom: '30px'
    },
    loadingState: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px',
      backgroundColor: '#fff',
      border: '1px solid #A7D7C5',
      borderRadius: '12px',
      marginBottom: '30px'
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: '3px solid #f3f3f3',
      borderTop: '3px solid #548170',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '16px'
    }
  };
  
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    }
  }, []);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const adminToken = localStorage.getItem('adminToken');
        if (!adminToken) {
          navigate('/admin/login');
          return;
        }
        
        const response = await axios.get(`${API_URL}/admin/dashboard`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });
        
        if (response.data.success) {
          setDashboardData({
            totalUsers: response.data.totalUsers || 0,
            totalProfessionals: response.data.totalProfessionals || 0,
            pendingVerifications: response.data.pendingVerifications || 0,
            journalEntries: response.data.journalEntries || 0,
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };
    
    fetchDashboardData();
  }, [navigate]);
  
  useEffect(() => {
    const fetchPendingDoctors = async () => {
      try {
        setLoading(true);
        const adminToken = localStorage.getItem('adminToken');
        if (!adminToken) {
          navigate('/admin/login');
          return;
        }
        
        const response = await axios.get(`${API_URL}/admin/doctors/pending`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });
        
        if (response.data.success && response.data.data) {
          setPendingDoctors(response.data.data);
          setDashboardData(prev => ({
            ...prev,
            pendingVerifications: response.data.data.length
          }));
        } else if (response.data && Array.isArray(response.data)) {
          setPendingDoctors(response.data);
        } else {
          setPendingDoctors([]);
        }
      } catch (error) {
        console.error('Error fetching pending doctors:', error);
        if (error.response?.status === 401) {
          navigate('/admin/login');
        } else {
          toast.error('Failed to load doctor applications');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchPendingDoctors();
  }, [navigate]);
  
  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminTokenExpiry');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };
  
  const handleViewDoctor = (id) => {
    setSelectedDoctorId(id);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDoctorId(null);
  };
  
  const handleModalApprove = (doctorId) => {
    setPendingDoctors(prevDoctors => prevDoctors.filter(doctor => doctor._id !== doctorId));
    setDashboardData(prev => ({
      ...prev,
      pendingVerifications: Math.max(0, prev.pendingVerifications - 1)
    }));
  };
  
  const handleModalReject = (doctorId) => {
    setPendingDoctors(prevDoctors => prevDoctors.filter(doctor => doctor._id !== doctorId));
    setDashboardData(prev => ({
      ...prev,
      pendingVerifications: Math.max(0, prev.pendingVerifications - 1)
    }));
  };
  
  const handleApproveDoctor = async (id) => {
    console.log('🔴 DASHBOARD - NOT WORKING:');
    console.log('  doctorId from parameter:', id);
    console.log('  doctorId type:', typeof id);
    console.log('  URL path:', window.location.pathname);

    try {
      console.log('🔍 Dashboard approving doctor:', id);
      
      await adminService.verifyDoctor(id, {
        verificationStatus: 'approved',
        verificationNotes: 'Approved from dashboard',
        rejectionReason: ''
      });
      
      console.log('✅ Dashboard approval SUCCESS');
      toast.success('Doctor approved successfully!');
      
      setPendingDoctors(prevDoctors => prevDoctors.filter(doctor => doctor._id !== id));
      
      setDashboardData(prev => ({
        ...prev,
        pendingVerifications: Math.max(0, prev.pendingVerifications - 1)
      }));
    } catch (err) {
      console.error('❌ Dashboard approval ERROR:', err);
      const errorMessage = err.response?.data?.message || 'Verification process failed. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleRejectDoctor = async (id) => {
    try {
      console.log('🔍 Dashboard rejecting doctor:', id);
      
      await adminService.verifyDoctor(id, {
        verificationStatus: 'rejected',
        verificationNotes: 'Rejected from dashboard',
        rejectionReason: 'Application rejected by admin'
      });
      
      console.log('✅ Dashboard rejection SUCCESS');
      toast.success('Doctor rejected successfully!');
      
      setPendingDoctors(prevDoctors => prevDoctors.filter(doctor => doctor._id !== id));
      
      setDashboardData(prev => ({
        ...prev,
        pendingVerifications: Math.max(0, prev.pendingVerifications - 1)
      }));
    } catch (err) {
      console.error('❌ Dashboard rejection ERROR:', err);
      const errorMessage = err.response?.data?.message || 'Verification process failed. Please try again.';
      toast.error(errorMessage);
    }
  };
  
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  const indexOfLastDoctor = currentPage * rowsPerPage;
  const indexOfFirstDoctor = indexOfLastDoctor - rowsPerPage;
  const currentDoctors = pendingDoctors.slice(indexOfFirstDoctor, indexOfLastDoctor);
  
  const totalPages = Math.ceil(pendingDoctors.length / rowsPerPage);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Admin Dashboard</h1>
      </div>
      
      <div className="stats-container">
        <div className="stat-card">
          <p className="stat-label">Total Users</p>
          <div className="admin-stat-value-container">
            <h2 className="admin-stat-value">{dashboardData.totalUsers}</h2>
          </div>
        </div>
        
        <div className="stat-card">
          <p className="stat-label">Total Professionals</p>
          <div className="admin-stat-value-container">
            <h2 className="admin-stat-value">{dashboardData.totalProfessionals}</h2>
          </div>
        </div>
        
        <div className="stat-card">
          <p className="stat-label">Pending Verifications</p>
          <div className="admin-stat-value-container">
            <h2 className="admin-stat-value">{dashboardData.pendingVerifications}</h2>
          </div>
        </div>
        
        <div className="stat-card">
          <p className="stat-label">Journal Entries</p>
          <div className="admin-stat-value-container">
            <h2 className="admin-stat-value">{dashboardData.journalEntries}</h2>
          </div>
        </div>
      </div>
      
      <div className="dashboard-section">
        <div className="admin-dashboard-divider"></div>
        <h2 className="admin-section-title">Doctor Applications Under Review</h2>
        
        {loading ? (
          <div style={styles.loadingState}>
            <div style={styles.loadingSpinner}></div>
            <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#666' }}>
              Loading doctor applications...
            </span>
          </div>
        ) : currentDoctors.length === 0 ? (
          <div style={styles.emptyState}>
            <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
              No pending doctor applications
            </h3>
            <p style={{ fontSize: '16px', color: '#666', margin: 0 }}>
              All applications have been reviewed.
            </p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHead}>
                  <th style={styles.tableHeadCellFirst}>Name</th>
                  <th style={styles.tableHeadCell}>Email</th>
                  <th style={styles.tableHeadCell}>Specialization</th>
                  <th style={styles.tableHeadCell}>Registration Date</th>
                  <th style={styles.tableHeadCellLast}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentDoctors.map((doctor, index) => {
                  const isEven = index % 2 === 1;
                  
                  return (
                    <tr key={doctor._id} style={isEven ? styles.tableRowAlt : styles.tableRow}>
                      <td style={styles.tableCellFirst}>
                        <div>
                          {doctor.firstName} {doctor.lastName}
                          {doctor.tenantName && (
                            <div style={styles.tenantInfo}>
                              ({doctor.tenantName})
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={styles.tableCell}>{doctor.email}</td>
                      <td style={styles.tableCell}>
                        {doctor.specialization || doctor.specialty || 'Not specified'}
                      </td>
                      <td style={styles.tableCell}>{formatDate(doctor.createdAt)}</td>
                      <td style={styles.tableCellLast}>
                        <button
                          style={styles.actionButton}
                          onClick={() => handleViewDoctor(doctor._id)}
                          title="View Doctor Details"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path 
                              d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" 
                              fill="#548170"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              className="page-nav prev"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  className={`page-number ${currentPage === index + 1 ? 'active' : ''}`}
                  onClick={() => paginate(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            
            <button 
              className="page-nav next"
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
        
        {pendingDoctors.length > 0 && (
          <div className="view-all">
            <button 
              onClick={() => navigate('/admin/professionals')}
              className="view-all-link"
            >
              View All Applications
            </button>
          </div>
        )}
      </div>

      <DoctorDetailsModal
        doctorId={selectedDoctorId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onApprove={handleModalApprove}
        onReject={handleModalReject}
      />
    </div>
  );
};

export default AdminDashboard;